import React, { useState, useEffect } from 'react';
import { persistentStorage } from './utils/storage';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import { StyledEngineProvider } from '@mui/material/styles';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import SignUp from './components/SignUp';
import Associateslogin from './components/AssociatesLogin';
import EventOrganizerLogin from './components/EventOrganizer/EventOrganizerLogin';
import EventOrganizerSignup from './components/EventOrganizer/EventOrganizerSignup';
import ValetServiceLogin from './components/ValetService/ValetServiceLogin';
import ValetServiceSignup from './components/ValetService/ValetServiceSignup';
import CompanyLegalDetails from './components/CompanyLegalDetails';
import EmailVerification from './components/EmailVerification';
import SignupSuccess from './components/SignupSuccess';
import MobileVerification from './components/MobileVerification';
import PasswordReset from './components/PasswordReset';
import PasswordSuccess from './components/PasswordSuccess';
import PersonalDetails from './components/PersonalDetails';
import VerificationPending from './components/VerificationPending';
import AwaitingAssignment from './components/AwaitingAssignment';
import HotelSelection from './components/HotelSelection';
import ResetPassword from './components/ResetPassword';
import SignupAs from './components/SignupAs';
import Navbar from './components/Info/Navbar';
import { pushNotificationManager } from './utils/pushNotifications';

// Screens load on first visit instead of all in one 2 MB bundle at startup,
// which cost low-end phones 1-2s of parsing before anything rendered. Login,
// signup and verification stay in the main bundle (first screens anyone sees,
// and auth code is frozen).
//
// A deploy replaces the hashed chunk files, so a phone still running the
// previous build can ask for a chunk that no longer exists. Reload once to
// pick up the new build instead of rendering a blank screen.
const CHUNK_RELOAD_KEY = 'chunkReloadAt';
function lazyWithRetry(factory) {
  return React.lazy(() => factory().catch((err) => {
    let last = 0;
    try { last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0); } catch (_) {}
    if (Date.now() - last > 10000) {
      try { sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now())); } catch (_) {}
      window.location.reload();
      return new Promise(() => {});
    }
    throw err;
  }));
}

function RouteLoading() {
  return (
    <div style={{ flex: 1, minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress size={32} />
    </div>
  );
}

const UserDashboard = lazyWithRetry(() => import('./components/User/UserDashboard'));
const ManagerDashboard = lazyWithRetry(() => import('./components/Manager/ManagerDashboard'));
const SupervisorDashboard = lazyWithRetry(() => import('./components/Manager/SuperVisorDashboard'));
const TosDashboard = lazyWithRetry(() => import('./components/TimeOfficeSupervisor/TosDashboard'));
const SupervisorPoolsPage = lazyWithRetry(() => import('./components/SupervisorPools/SupervisorPoolsPage'));
const Dashboard = lazyWithRetry(() => import('./components/ValetDashboard'));
const NewBooking = lazyWithRetry(() => import('./components/NewBooking'));
const ParkingDetails = lazyWithRetry(() => import('./components/User/ParkingDetails'));
const Payment = lazyWithRetry(() => import('./components/User/Payment'));
const Profile = lazyWithRetry(() => import('./components/User/Profile'));
const Notifications = lazyWithRetry(() => import('./components/User/Notifications'));
const PropertyInformation = lazyWithRetry(() => import('./components/PropertyInformation'));
const EventOrganizerDashboard = lazyWithRetry(() => import('./components/EventOrganizer/EventOrganizerDashboard'));
const EventsAll = lazyWithRetry(() => import('./components/EventOrganizer/EventsAll'));
const EventCreate = lazyWithRetry(() => import('./components/EventOrganizer/EventCreate'));
const EOEventDashboard = lazyWithRetry(() => import('./components/EventOrganizer/EOEventDashboard'));
const EventEdit = lazyWithRetry(() => import('./components/EventOrganizer/EventEdit'));
const EventFindVsp = lazyWithRetry(() => import('./components/EventOrganizer/EventFindVsp'));
const EoSupervisorsPage = lazyWithRetry(() => import('./components/EventOrganizer/EoSupervisorsPage'));
const PropertyCreate = lazyWithRetry(() => import('./components/EventOrganizer/PropertyCreate'));
const PropertyEmployeeManagement = lazyWithRetry(() => import('./components/EventOrganizer/PropertyEmployeeManagement'));
const PropertyEmployeeList = lazyWithRetry(() => import('./components/EventOrganizer/PropertyEmployeeList'));
const PropertyDashboard = lazyWithRetry(() => import('./components/shared/PropertyOps/PropertyDashboard'));
const CreatorPendingApprovals = lazyWithRetry(() => import('./components/shared/PropertyOps/CreatorPendingApprovals'));
const HelpPage = lazyWithRetry(() => import('./components/shared/HelpPage'));
const EoBookingHistoryPicker = lazyWithRetry(() => import('./components/EventOrganizer/EoBookingHistoryPicker'));
const VspBookingHistoryPicker = lazyWithRetry(() => import('./components/ValetService/VspBookingHistoryPicker'));
const EoNotifications = lazyWithRetry(() => import('./components/EventOrganizer/EoNotifications'));
const ValetServiceLayout = lazyWithRetry(() => import('./components/ValetService/ValetServiceLayout'));
const VspManagerHome = lazyWithRetry(() => import('./components/ValetService/VspManagerHome'));
const VspNotifications = lazyWithRetry(() => import('./components/ValetService/VspNotifications'));
const VspAddTimeOfficeSupervisor = lazyWithRetry(() => import('./components/ValetService/VspAddTimeOfficeSupervisor'));
const VspEventCreate = lazyWithRetry(() => import('./components/ValetService/VspEventCreate'));
const VspEventEdit = lazyWithRetry(() => import('./components/ValetService/VspEventEdit'));
const VspEventsPage = lazyWithRetry(() => import('./components/ValetService/VspEventsPage'));
const VspEmployeePool = lazyWithRetry(() => import('./components/ValetService/VspEmployeePool'));
const VspPendingApprovals = lazyWithRetry(() => import('./components/ValetService/VspPendingApprovals'));
const ManagerAssignment = lazyWithRetry(() => import('./components/ManagerAssignment'));
const AssignedTickets = lazyWithRetry(() => import('./components/AssignedTickets'));
const AssignedTicketDetails = lazyWithRetry(() => import('./components/AssignedTicketDetails'));
const ValetAssignedTickets = lazyWithRetry(() => import('./components/ValetAssignedTickets'));
const UpdateSlot = lazyWithRetry(() => import('./components/UpdateSlot'));
const KeyHandover = lazyWithRetry(() => import('./components/KeyHandover'));
const Handover = lazyWithRetry(() => import('./components/Handover'));
const EmployeeStatusPage = lazyWithRetry(() => import('./components/Employeees'));
const BookingsPage = lazyWithRetry(() => import('./components/Bookings'));
const DeleteAccountInfo = lazyWithRetry(() => import('./components/DeleteAccountInfo'));
const PrivacyPolicy = lazyWithRetry(() => import('./components/PrivacyPolicy'));
const TermsOfService = lazyWithRetry(() => import('./components/TermsOfService'));
const ParkingTerms = lazyWithRetry(() => import('./components/ParkingTerms'));
const AboutUs = lazyWithRetry(() => import('./components/AboutUs'));
const Support = lazyWithRetry(() => import('./components/Support'));
const Home = lazyWithRetry(() => import('./components/Info/Home'));
const Features = lazyWithRetry(() => import('./components/Info/Features'));
const Technology = lazyWithRetry(() => import('./components/Info/Technology'));
const Contact = lazyWithRetry(() => import('./components/Info/Contact'));
const OperationsDashboard = lazyWithRetry(() => import('./components/Operations/OperationsDashboard'));

// Theme configuration
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
    },
  },
});

// Renders nothing for the first `delayMs`, then a centred "still working on it"
// message with a way out. Every gate below (auth check, root redirect, storage
// hydration) used to `return null`, so whenever the async work behind the gate
// hung — no network, a rejected storage read on native, a redirect that never
// resolved — the user was left staring at a permanently blank screen with no
// recovery short of force-closing the app.
const DelayedFallback = ({ delayMs = 8000, message = 'Taking longer than usual…' }) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShow(true), delayMs);
    return () => clearTimeout(t);
  }, [delayMs]);

  if (!show) return null;

  return (
    <div
      style={{
        minHeight: '100dvh',
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '24px',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'
      }}
    >
      <p style={{ margin: '0 0 20px', fontSize: '15px', color: '#555' }}>{message}</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          appearance: 'none',
          border: 'none',
          borderRadius: '8px',
          padding: '12px 24px',
          fontSize: '15px',
          fontWeight: 600,
          color: '#fff',
          background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
          cursor: 'pointer',
          minWidth: '140px'
        }}
      >
        Reload
      </button>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = checking, true/false = result
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if this is /user-dashboard with uniqueID query param (supports both 'uniqueid' and 'src') - allow access for auto-login
        const searchParams = new URLSearchParams(location.search);
        const uniqueID = searchParams.get('uniqueid') || searchParams.get('src');
        const isUserDashboardWithUniqueID = location.pathname === '/user-dashboard' && uniqueID;
        
        if (isUserDashboardWithUniqueID) {
          // Allow access - UserDashboard will handle auto-login
          setIsAuthenticated(true);
          setIsChecking(false);
          return;
        }

        // Import persistent storage
        const { persistentStorage } = await import('./utils/storage');
        const { isSessionValid } = await import('./utils/session');

        // Restore X-User-Id axios header from persisted state so backend
        // authorization helpers keep working across hard refreshes.
        try {
          const axios = (await import('axios')).default;
          const persistedUserId = await persistentStorage.get('currentUserId');
          if (persistedUserId) {
            axios.defaults.headers.common['X-User-Id'] = String(persistedUserId);
          }
        } catch (_) { /* non-fatal */ }

        const pathname = location.pathname.toLowerCase();
        const sessionOk = await isSessionValid();
        let authenticated = false;

        // Check authentication based on route type
        if (pathname.startsWith('/event-organizer/')) {
          // Event Organizer routes - check for agency authentication (backend uses agency_id)
          const currentAgencyId = await persistentStorage.get('currentAgencyId');
          const currentAgency = await persistentStorage.get('currentAgency');
          // Per-event dashboard URLs are also reachable by event-scoped associates (e.g. TOS).
          const isEventScopedPath = /^\/event-organizer\/events\/\d+/.test(pathname);
          let eventScopedOk = false;
          if (isEventScopedPath) {
            const currentEventId = await persistentStorage.get('currentEventId');
            const currentUserId = await persistentStorage.get('currentUserId');
            eventScopedOk = !!(currentEventId && currentUserId);
          }
          authenticated = sessionOk && (currentAgencyId || currentAgency || eventScopedOk);
          if (!authenticated) {
            navigate('/associates-login', { replace: true });
            return;
          }
        } else if (pathname.startsWith('/valet-service/')) {
          // Valet Service routes - check for valet service authentication
          const currentValetServiceId = await persistentStorage.get('currentValetServiceId');
          const currentValetService = await persistentStorage.get('currentValetService');
          authenticated = sessionOk && (currentValetServiceId || currentValetService);
          if (!authenticated) {
            navigate('/associates-login', { replace: true });
            return;
          }
        } else {
          // Customer/User routes - check for customer authentication.
          // A few detail pages (assigned-tickets, assigned-ticket-details) are
          // shared across user, EO, and VSP scopes — VSP/EO open them from
          // BookingHistoryView, but their sessions don't set currentUser*.
          // Accept any of the three credential types on those shared paths.
          const SHARED_CROSS_ROLE_PATHS = ['/assigned-ticket-details', '/assigned-tickets'];
          const isSharedCrossRolePath = SHARED_CROSS_ROLE_PATHS.includes(pathname);
          const currentUserPhone = await persistentStorage.get('currentUserPhone');
          const currentUserEmail = await persistentStorage.get('currentUserEmail');
          const currentUser = await persistentStorage.get('currentUser');
          let crossRoleOk = false;
          if (isSharedCrossRolePath) {
            const currentValetServiceId = await persistentStorage.get('currentValetServiceId');
            const currentValetService = await persistentStorage.get('currentValetService');
            const currentAgencyId = await persistentStorage.get('currentAgencyId');
            const currentAgency = await persistentStorage.get('currentAgency');
            crossRoleOk = !!(currentValetServiceId || currentValetService || currentAgencyId || currentAgency);
          }
          authenticated = sessionOk && (currentUserPhone || currentUserEmail || currentUser || crossRoleOk);
          if (!authenticated) {
            navigate('/login', { replace: true });
            return;
          }
        }

        setIsAuthenticated(authenticated);
      } catch (error) {
        console.error('Error checking authentication:', error);
        // Fallback to localStorage for web
        const pathname = location.pathname.toLowerCase();
        const expiresAtStr = localStorage.getItem('sessionExpiresAt');
        let sessionOk = false;
        if (expiresAtStr) {
          const exp = Number(expiresAtStr);
          sessionOk = !Number.isNaN(exp) && Date.now() < exp;
        }
        let authenticated = false;

        if (pathname.startsWith('/event-organizer/')) {
          const currentAgencyId = localStorage.getItem('currentAgencyId');
          const currentAgency = localStorage.getItem('currentAgency');
          const isEventScopedPath = /^\/event-organizer\/events\/\d+/.test(pathname);
          let eventScopedOk = false;
          if (isEventScopedPath) {
            const currentEventId = localStorage.getItem('currentEventId');
            const currentUserId = localStorage.getItem('currentUserId');
            eventScopedOk = !!(currentEventId && currentUserId);
          }
          authenticated = sessionOk && (currentAgencyId || currentAgency || eventScopedOk);
          if (!authenticated) {
            navigate('/associates-login', { replace: true });
            return;
          }
        } else if (pathname.startsWith('/valet-service/')) {
          const currentValetServiceId = localStorage.getItem('currentValetServiceId');
          const currentValetService = localStorage.getItem('currentValetService');
          authenticated = sessionOk && (currentValetServiceId || currentValetService);
          if (!authenticated) {
            navigate('/associates-login', { replace: true });
            return;
          }
        } else {
          // Shared cross-role detail pages — mirror primary-branch logic.
          const SHARED_CROSS_ROLE_PATHS = ['/assigned-ticket-details', '/assigned-tickets'];
          const isSharedCrossRolePath = SHARED_CROSS_ROLE_PATHS.includes(pathname);
          const currentUserPhone = localStorage.getItem('currentUserPhone');
          const currentUserEmail = localStorage.getItem('currentUserEmail');
          const currentUser = localStorage.getItem('currentUser');
          let crossRoleOk = false;
          if (isSharedCrossRolePath) {
            const currentValetServiceId = localStorage.getItem('currentValetServiceId');
            const currentValetService = localStorage.getItem('currentValetService');
            const currentAgencyId = localStorage.getItem('currentAgencyId');
            const currentAgency = localStorage.getItem('currentAgency');
            crossRoleOk = !!(currentValetServiceId || currentValetService || currentAgencyId || currentAgency);
          }
          authenticated = sessionOk && (currentUserPhone || currentUserEmail || currentUser || crossRoleOk);
          if (!authenticated) {
            navigate('/login', { replace: true });
            return;
          }
        }
        
        setIsAuthenticated(authenticated);
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [navigate, location]);

  // Show nothing while checking (prevents flash of login page). If the check
  // never resolves, DelayedFallback surfaces a reload escape hatch.
  if (isChecking || isAuthenticated === null) {
    return <DelayedFallback />;
  }

  // Unauthenticated: checkAuth already issued the navigate() for every path it
  // rejects, so this branch only happens if that redirect never landed. Don't
  // change the auth decision — just don't render a permanently blank screen.
  if (!isAuthenticated) {
    return <DelayedFallback />;
  }

  return children;
};

// Root redirect component with TTL
const RootIndex = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const go = async () => {
      try {
        const { persistentStorage } = await import('./utils/storage');
        const { isSessionValid } = await import('./utils/session');
        const sessionOk = await isSessionValid();
        const phone = await persistentStorage.get('currentUserPhone');
        const email = await persistentStorage.get('currentUserEmail');
        const user = await persistentStorage.get('currentUser');
        if (sessionOk && (phone || email || user)) {
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/login', { replace: true });
        }
      } catch {
        const expStr = localStorage.getItem('sessionExpiresAt');
        const exp = expStr ? Number(expStr) : 0;
        const sessionOk = exp && Date.now() < exp;
        const phone = localStorage.getItem('currentUserPhone');
        const email = localStorage.getItem('currentUserEmail');
        const user = localStorage.getItem('currentUser');
        if (sessionOk && (phone || email || user)) {
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/login', { replace: true });
        }
      }
    };
    go();
  }, [navigate]);
  return <DelayedFallback />;
};

// Dashboard redirect that derives role from stored user
const DashboardRedirect = () => {
  const [resolved, setResolved] = useState(false);
  const [dest, setDest] = useState('/login');
  const navigate = useNavigate();
  useEffect(() => {
    const resolve = async () => {
      try {
        const { persistentStorage } = await import('./utils/storage');
        // Prefer full user object
        const userStr = await persistentStorage.get('currentUser');
        if (userStr) {
          try {
            const u = JSON.parse(userStr);
            if (u && u.role) {
              const r = String(u.role);
              setDest(r === 'user' || r === 'customer' ? '/user-dashboard'
                : r === 'manager' ? '/manager-dashboard'
                : r === 'time_office_supervisor' ? '/tos-dashboard'
                : (r === 'supervisor') ? '/supervisor-dashboard'
                : (r === 'valet' || r === 'serviceboy') ? '/valet-dashboard'
                : r === 'agency' ? '/event-organizer/dashboard'
                : r === 'valet_service' ? '/valet-service/dashboard'
                : '/login');
              setResolved(true);
              return;
            }
          } catch {}
        }
        const cr = await persistentStorage.get('currentRole');
        if (cr) {
          const r = String(cr);
          setDest(r === 'user' || r === 'customer' ? '/user-dashboard'
            : r === 'manager' ? '/manager-dashboard'
            : r === 'time_office_supervisor' ? '/tos-dashboard'
            : (r === 'supervisor') ? '/supervisor-dashboard'
            : (r === 'valet' || r === 'serviceboy') ? '/valet-dashboard'
            : '/login');
          setResolved(true);
          return;
        }
      } catch {
        // Fallback to localStorage
        try {
          const userStr = localStorage.getItem('currentUser');
          if (userStr) {
            const u = JSON.parse(userStr);
            if (u && u.role) {
              const r = String(u.role);
              setDest(r === 'user' || r === 'customer' ? '/user-dashboard'
                : r === 'manager' ? '/manager-dashboard'
                : r === 'time_office_supervisor' ? '/tos-dashboard'
                : (r === 'supervisor') ? '/supervisor-dashboard'
                : (r === 'valet' || r === 'serviceboy') ? '/valet-dashboard'
                : r === 'agency' ? '/event-organizer/dashboard'
                : r === 'valet_service' ? '/valet-service/dashboard'
                : '/login');
              setResolved(true);
              return;
            }
          }
        } catch {}
        const cr = localStorage.getItem('currentRole');
        if (cr) {
          const r = String(cr);
          setDest(r === 'user' || r === 'customer' ? '/user-dashboard'
            : r === 'manager' ? '/manager-dashboard'
            : r === 'time_office_supervisor' ? '/tos-dashboard'
            : (r === 'supervisor') ? '/supervisor-dashboard'
            : (r === 'valet' || r === 'serviceboy') ? '/valet-dashboard'
            : '/login');
          setResolved(true);
          return;
        }
      }
      setResolved(true);
    };
    resolve();
  }, [navigate]);
  if (!resolved) return null;
  return <Navigate to={dest} replace />;
};
// Landing Page Layout Component
const LandingPageLayout = () => {
  return (
    <>
      <Navbar />
      <Outlet />
    </>
  );
};

const App = () => {
  const [role, setRole] = useState(null);

  // On native, the WebView's localStorage is wiped on app relaunch while
  // Capacitor Preferences persists. The app reads session/identity from
  // localStorage synchronously in many places, so mirror Preferences ->
  // localStorage BEFORE rendering any route — otherwise those reads come back
  // null on reopen and the dashboards show "Session Expired". No-op on web
  // (starts already hydrated).
  const [storageHydrated, setStorageHydrated] = useState(!Capacitor.isNativePlatform());
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    let cancelled = false;
    persistentStorage.hydrate().finally(() => {
      if (!cancelled) setStorageHydrated(true);
    });
    return () => { cancelled = true; };
  }, []);

  // Initialize StatusBar for mobile apps
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      StatusBar.setStyle({ style: Style.Default });
      StatusBar.setBackgroundColor({ color: '#ffffff' });
      StatusBar.setOverlaysWebView({ overlay: false });
    }
  }, []);

  useEffect(() => {
    // Attach listeners at boot; sync the token now if already logged in.
    pushNotificationManager.initialize().then(async () => {
      const uid = await persistentStorage.get('currentUserId');
      if (uid) pushNotificationManager.syncTokenForUser();
    });
  }, []);

  useEffect(() => {
    // Fresh login flips `role`; request permission + register the token now
    // that the user id is in storage.
    if (role) pushNotificationManager.syncTokenForUser();
  }, [role]);

  const ScrollToTop = () => {
    const { pathname } = useLocation();

    useEffect(() => {
      window.scrollTo(0, 0);
    }, [pathname]);

    return null;
  };

  // Hold render on native until the localStorage mirror is restored from
  // Preferences, so no route/dashboard reads an empty localStorage on reopen.
  if (!storageHydrated) return <DelayedFallback />;

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div style={{
          minHeight: '100dvh', // Dynamic viewport height for mobile
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)'
        }}>
          <Router>
            <ScrollToTop />
            <React.Suspense fallback={<RouteLoading />}>
            <Routes>
            {/* Root: redirect based on TTL + stored auth */}
            <Route path="/" element={<RootIndex />} />
            
            {/* Landing Page Routes with Navbar */}
            <Route element={<LandingPageLayout />}>
              <Route path="/home" element={<Home />} />
              <Route path="/features" element={<Features />} />
              <Route path="/technology" element={<Technology />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/support" element={<Support />} />
            </Route>
            
            {/* App Routes */}
            <Route path="/*" element={
              <Routes>
                <Route path="/login" element={<Login setRole={setRole} />} />
                <Route path="/associates-login" element={<Associateslogin setRole={setRole} />} />
                <Route path="/signup-as" element={<SignupAs />} />
                <Route path="/property-information" element={<PropertyInformation />} />
                <Route path="/company-legal-details" element={<CompanyLegalDetails />} />
                <Route path="/email-verification" element={<EmailVerification />} />
                <Route path="/manager-assignment" element={<ManagerAssignment />} />
                <Route path="/signup-success" element={<SignupSuccess />} />
                <Route path="/mobile-verification" element={<MobileVerification />} />
                <Route path="/password-reset" element={<PasswordReset />} />
                <Route path="/password-success" element={<PasswordSuccess />} />
                <Route path="/personal-details" element={<PersonalDetails />} />
                <Route path="/verification-pending" element={<VerificationPending />} />
                <Route path="/awaiting-assignment" element={<AwaitingAssignment />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/delete-account" element={<DeleteAccountInfo />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/terms" element={<ParkingTerms />} />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/operations" element={<OperationsDashboard />} />
                
                {/* Event Organizer Routes */}
                <Route path="/event-organizer/login" element={<Navigate to="/associates-login" replace />} />
                <Route path="/event-organizer/signup" element={<EventOrganizerSignup />} />
                <Route path="/event-organizer/dashboard" element={
                  <ProtectedRoute>
                    <EventOrganizerDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/event-organizer/events" element={
                  <ProtectedRoute>
                    <EventsAll />
                  </ProtectedRoute>
                } />
                <Route path="/event-organizer/events/new" element={
                  <ProtectedRoute>
                    <EventCreate />
                  </ProtectedRoute>
                } />
                <Route path="/event-organizer/events/:eventId" element={
                  <ProtectedRoute>
                    <EOEventDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/event-organizer/events/:eventId/edit" element={
                  <ProtectedRoute>
                    <EventEdit />
                  </ProtectedRoute>
                } />
                <Route path="/event-organizer/events/:eventId/find-vsp" element={
                  <ProtectedRoute>
                    <EventFindVsp />
                  </ProtectedRoute>
                } />
                <Route path="/event-organizer/time-office-supervisors" element={
                  <ProtectedRoute>
                    <EoSupervisorsPage />
                  </ProtectedRoute>
                } />
                <Route path="/event-organizer/properties/new" element={
                  <ProtectedRoute>
                    <PropertyCreate role="eo" />
                  </ProtectedRoute>
                } />
                <Route path="/event-organizer/properties/:propertyId/edit" element={
                  <ProtectedRoute>
                    <PropertyCreate editMode role="eo" />
                  </ProtectedRoute>
                } />
                <Route path="/event-organizer/properties/:propertyId" element={
                  <ProtectedRoute>
                    <PropertyDashboard role="eo" />
                  </ProtectedRoute>
                } />
                <Route path="/event-organizer/properties/:propertyId/employees" element={
                  <ProtectedRoute>
                    <PropertyEmployeeManagement />
                  </ProtectedRoute>
                } />
                <Route path="/event-organizer/properties/:propertyId/employees/:role" element={
                  <ProtectedRoute>
                    <PropertyEmployeeList />
                  </ProtectedRoute>
                } />
                <Route path="/event-organizer/pending-approvals" element={
                  <ProtectedRoute>
                    <CreatorPendingApprovals role="eo" />
                  </ProtectedRoute>
                } />
                <Route path="/event-organizer/booking-history" element={
                  <ProtectedRoute>
                    <EoBookingHistoryPicker />
                  </ProtectedRoute>
                } />
                <Route path="/event-organizer/notifications" element={
                  <ProtectedRoute>
                    <EoNotifications />
                  </ProtectedRoute>
                } />
                <Route path="/event-organizer/help" element={
                  <ProtectedRoute>
                    <HelpPage backTo="/event-organizer/dashboard" />
                  </ProtectedRoute>
                } />
                
                {/* Valet Service Routes */}
                <Route path="/valet-service/login" element={<Navigate to="/associates-login" replace />} />
                <Route path="/valet-service/signup" element={<ValetServiceSignup />} />
                <Route path="/valet-service" element={
                  <ProtectedRoute>
                    <ValetServiceLayout />
                  </ProtectedRoute>
                }>
                  <Route index element={<Navigate to="dashboard" replace />} />
                  <Route path="dashboard" element={<VspManagerHome />} />
                  <Route path="events" element={<VspEventsPage />} />
                  <Route path="notifications" element={<VspNotifications />} />
                  <Route path="create-event" element={<VspEventCreate />} />
                  <Route path="events/:eventId/edit" element={<VspEventEdit />} />
                  <Route path="events/:eventId/add-to-supervisor" element={<VspAddTimeOfficeSupervisor />} />
                  <Route path="events/:eventId" element={<EOEventDashboard role="vsp" />} />
                  <Route path="properties/new" element={<PropertyCreate role="vsp" />} />
                  <Route path="properties/:propertyId/edit" element={<PropertyCreate editMode role="vsp" />} />
                  <Route path="properties/:propertyId" element={<PropertyDashboard role="vsp" />} />
                  <Route path="properties/:propertyId/employees" element={<PropertyEmployeeManagement />} />
                  <Route path="properties/:propertyId/employees/:role" element={<PropertyEmployeeList />} />
                  <Route path="pending-approvals" element={<VspPendingApprovals />} />
                  <Route path="booking-history" element={<VspBookingHistoryPicker />} />
                  <Route path="help" element={<HelpPage backTo="/valet-service/dashboard" />} />
                  <Route path="employees" element={<VspEmployeePool />} />
                  {/* Old /valet-service/staff URL now points at the same pool UI so existing links don't 404. */}
                  <Route path="staff" element={<VspEmployeePool />} />
                </Route>
                
                {/* Protected Routes */}
                <Route path="/user-dashboard" element={
                  <ProtectedRoute>
                    <UserDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/manager-dashboard" element={
                  <ProtectedRoute>
                    <ManagerDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/supervisor-dashboard" element={
                  <ProtectedRoute>
                    <SupervisorDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/tos-dashboard" element={
                  <ProtectedRoute>
                    <TosDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/supervisor-pools" element={
                  <ProtectedRoute>
                    <SupervisorPoolsPage />
                  </ProtectedRoute>
                } />
                <Route path="/assigned-tickets" element={
                  <ProtectedRoute>
                    <AssignedTickets />
                  </ProtectedRoute>
                } />
                <Route path="/assigned-ticket-details" element={
                  <ProtectedRoute>
                    <AssignedTicketDetails />
                  </ProtectedRoute>
                } />
                <Route path="/valet-dashboard" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="/valet-assigned-tickets" element={
                  <ProtectedRoute>
                    <ValetAssignedTickets />
                  </ProtectedRoute>
                } />
                <Route path="/update-slot" element={
                  <ProtectedRoute>
                    <UpdateSlot />
                  </ProtectedRoute>
                } />
                <Route path="/key-handover" element={
                  <ProtectedRoute>
                    <KeyHandover />
                  </ProtectedRoute>
                } />
                <Route path="/handover" element={
                  <ProtectedRoute>
                    <Handover />
                  </ProtectedRoute>
                } />
                <Route path="/hotel-selection" element={<HotelSelection />} />
                <Route path="/new-booking" element={
                  <ProtectedRoute>
                    <NewBooking />
                  </ProtectedRoute>
                } />
                <Route path="/parking-details" element={
                  <ProtectedRoute>
                    <ParkingDetails />
                  </ProtectedRoute>
                } />
                <Route path="/payment" element={
                  <ProtectedRoute>
                    <Payment />
                  </ProtectedRoute>
                } />
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                } />
                <Route path="/notifications" element={
                  <ProtectedRoute>
                    <Notifications />
                  </ProtectedRoute>
                } />
                <Route path="/employee-status" element={
                  <ProtectedRoute>
                    <EmployeeStatusPage />
                  </ProtectedRoute>
                } />
                <Route path="/employee-status-valet" element={
                  <ProtectedRoute>
                    <EmployeeStatusPage filterBy="valets" />
                  </ProtectedRoute>
                } />
                <Route path="/bookings" element={
                  <ProtectedRoute>
                    <BookingsPage />
                  </ProtectedRoute>
                } />

                {/* Role-based redirection using stored user/role */}
                <Route path="/dashboard" element={<DashboardRedirect />} />
              </Routes>
            } />
          </Routes>
            </React.Suspense>
        </Router>
        </div>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default App;
